import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";

import CustomNavbar from "../../components/common/CustomNavbar";
import PageLayout from "../../components/layouts/PageLayout";

import { IoArrowBack } from "react-icons/io5";
import {
    LuBrain, LuArrowUp, LuCircleCheck, LuCopy, LuCheck,
    LuTrash2, LuLoaderCircle, LuTerminal,
} from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { executeAIChatQuery, executeAIQuery } from "../../features/ai-studio/AIStudioServices";
import { JSON_COLORS } from "../../util/Constants";

interface Message {
    id: number;
    role: "user" | "ai";
    text?: string;
    response?: any;
    isError?: boolean;
    time: string;
    copied?: boolean;
}

function syntaxHighlight(json: string): string {
    return json.replace(
        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
        (match) => {
            if (/^"/.test(match)) {
                if (/:$/.test(match)) {
                    return `<span class="${JSON_COLORS.key}">${match}</span>`;
                }

                return `<span class="${JSON_COLORS.string}">${match}</span>`;
            }

            if (/true|false/.test(match)) {
                return `<span class="${JSON_COLORS.boolean}">${match}</span>`;
            }

            if (/null/.test(match)) {
                return `<span class="${JSON_COLORS.null}">${match}</span>`;
            }

            return `<span class="${JSON_COLORS.number}">${match}</span>`;
        }
    );
}

let msgIdCounter = 0;

export default function AddAIQuery() {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();

    const [query, setQuery] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [messages, setMessages] = useState<Message[]>([]);
    const [copiedId, setCopiedId] = useState<number | null>(null);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const now = () =>
        new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const autoResize = () => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = Math.min(el.scrollHeight, 120) + "px";
    };

    const handleCopy = useCallback((id: number, text: string) => {
        navigator.clipboard.writeText(text).catch(() => { });
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    }, []);

    const handleClear = () => setMessages([]);

    const handleSubmit = async () => {
        if (!query.trim() || submitting) return;
        const currentQuery = query.trim();

        setMessages((prev) => [
            ...prev,
            { id: ++msgIdCounter, role: "user", text: currentQuery, time: now() },
        ]);
        setQuery("");
        if (textareaRef.current) textareaRef.current.style.height = "auto";
        setSubmitting(true);

        try {
            const result = await dispatch(executeAIChatQuery({ query: currentQuery })).unwrap();
            setMessages((prev) => [
                ...prev,
                { id: ++msgIdCounter, role: "ai", response: result, time: now() },
            ]);
        } catch (err: any) {
            setMessages((prev) => [
                ...prev,
                {
                    id: ++msgIdCounter,
                    role: "ai",
                    text: err?.message || "Failed to execute query",
                    isError: true,
                    time: now(),
                },
            ]);
            toast.error(err?.message || "Failed to execute query");
        } finally {
            setSubmitting(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    const getRowCount = (response: any): number | null => {
        if (response?.data && Array.isArray(response.data)) return response.data.length;
        return null;
    };

    return (
        <>
            <PageLayout
                customNavbar={
                    <CustomNavbar>
                        <div className="flex gap-3 items-center">
                            <IoArrowBack
                                className="text-2xl cursor-pointer hover:text-brand-main transition"
                                onClick={() => navigate(-1)}
                            />
                            <span className="font-bold text-lg uppercase">AI Prompt</span>
                        </div>
                    </CustomNavbar>
                }
            >
                <div className="bg-white rounded-3xl border border-gray-100 flex flex-col h-[calc(100vh-180px)]">
                    {/* ── Header — sticky at top ── */}
                    <div className="sticky top-0 z-10 flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-white">
                        <div className="w-7 h-7 rounded-full bg-brand-main/10 flex items-center justify-center">
                            <LuBrain className="text-sm text-brand-main" />
                        </div>
                        <span className="font-semibold text-sm text-gray-800 flex-1">AI Prompt</span>

                        {messages.length > 0 && (
                            <button
                                onClick={handleClear}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                            >
                                <LuTrash2 className="text-sm" />
                                Clear
                            </button>
                        )}
                    </div>

                    {/* ── Messages — normal flow, grows with content ── */}
                    <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-4">
                        {messages.length === 0 && (
                            <div className="flex flex-col items-center justify-center gap-2 text-gray-400 text-sm py-20">
                                <LuTerminal className="text-2xl" />
                                <span>Run a query to see results</span>
                            </div>
                        )}

                        {messages.map((msg) => (
                            <div
                                key={msg.id}
                                className={`flex gap-2 items-start ${msg.role === "user" ? "flex-row-reverse" : ""}`}
                            >
                                {/* Avatar */}
                                <div
                                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${msg.role === "ai"
                                        ? "bg-gray-100 text-gray-500 border border-gray-200"
                                        : "bg-gray-900 text-white"
                                        }`}
                                >
                                    {msg.role === "ai" ? "✦" : "U"}
                                </div>

                                {/* Bubble wrapper */}
                                <div
                                    className={`flex flex-col gap-1 max-w-[85%] ${msg.role === "user" ? "items-end" : "items-start"
                                        }`}
                                >
                                    {/* User bubble */}
                                    {msg.role === "user" && (
                                        <div className="px-3 py-2 text-sm rounded-2xl rounded-tr-sm bg-gray-50 border border-gray-100 text-gray-800">
                                            {msg.text}
                                        </div>
                                    )}

                                    {/* AI error bubble */}
                                    {msg.role === "ai" && msg.isError && (
                                        <div className="px-3 py-2.5 text-sm rounded-2xl rounded-tl-sm bg-red-50 border border-red-100 text-red-700 flex items-start gap-2">
                                            <LuLoaderCircle className="text-base mt-0.5 shrink-0 text-red-500" />
                                            <span>{msg.text}</span>
                                        </div>
                                    )}

                                    {/* AI response bubble */}
                                    {msg.role === "ai" && msg.response && (
                                        <div className="rounded-2xl rounded-tl-sm border border-gray-100 overflow-hidden bg-white text-sm">
                                            {getRowCount(msg.response) !== null && (
                                                <div className="flex items-center gap-1.5 px-3 pt-2.5 text-xs text-green-600">
                                                    <LuCircleCheck className="text-sm" />
                                                    {getRowCount(msg.response)} row{getRowCount(msg.response) !== 1 ? "s" : ""} returned
                                                </div>
                                            )}

                                            <div className="flex items-center justify-between px-3 py-1.5 mt-2 bg-gray-50 border-t border-b border-gray-100">
                                                <span className="text-xs text-gray-400 font-mono">json</span>
                                                <button
                                                    onClick={() =>
                                                        handleCopy(
                                                            msg.id,
                                                            JSON.stringify(msg.response, null, 2)
                                                        )
                                                    }
                                                    className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition ${copiedId === msg.id
                                                        ? "border-green-200 text-green-600 bg-green-50"
                                                        : "border-gray-200 text-gray-500 hover:bg-white"
                                                        }`}
                                                >
                                                    {copiedId === msg.id ? (
                                                        <><LuCheck className="text-xs" />Copied</>
                                                    ) : (
                                                        <><LuCopy className="text-xs" />Copy</>
                                                    )}
                                                </button>
                                            </div>

                                            <pre
                                                className="text-xs font-mono px-3 py-2.5 overflow-x-auto whitespace-pre leading-relaxed"
                                                dangerouslySetInnerHTML={{
                                                    __html: syntaxHighlight(
                                                        JSON.stringify(msg.response, null, 2)
                                                    ),
                                                }}
                                            />
                                        </div>
                                    )}

                                    <span className="text-[11px] text-gray-400 px-0.5">{msg.time}</span>
                                </div>
                            </div>
                        ))}

                        {/* Typing indicator */}
                        {submitting && (
                            <div className="flex gap-2 items-start">
                                <div className="w-6 h-6 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-xs text-gray-500 shrink-0">
                                    ✦
                                </div>
                                <div className="px-3 py-2.5 bg-white border border-gray-100 rounded-2xl rounded-tl-sm flex gap-1 items-center">
                                    {[0, 1, 2].map((n) => (
                                        <span
                                            key={n}
                                            className="w-1.5 h-1.5 rounded-full bg-gray-400"
                                            style={{
                                                animation: `pulse 1.2s ease-in-out ${n * 0.2}s infinite`,
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>
                    <div className="flex-shrink-0 bg-white border-t border-gray-100 px-3 py-3 flex gap-2 items-end">
                        <textarea
                            ref={textareaRef}
                            rows={1}
                            value={query}
                            onChange={(e) => { setQuery(e.target.value); autoResize(); }}
                            onKeyDown={handleKeyDown}
                            placeholder="Enter prompt or SQL query… (Enter to send)"
                            className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none resize-none focus:ring-2 focus:ring-brand-main/20 focus:border-brand-main/40 transition leading-relaxed"
                            style={{ minHeight: "38px", maxHeight: "110px" }}
                        />
                        <button
                            onClick={handleSubmit}
                            disabled={submitting || !query.trim()}
                            className="w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center shrink-0 hover:opacity-80 transition disabled:opacity-25 disabled:cursor-not-allowed"
                        >
                            <LuArrowUp className="text-sm" />
                        </button>
                    </div>

                </div>
            </PageLayout>
        </>
    );
}